import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { ArrowLeft, Swords, Trophy, TrendingUp, TrendingDown, Plus, Users } from 'lucide-react';
import { getRivals, getCachedName, getMatches } from '../lib/localStore';
import BottomNav from '../components/BottomNav';

export default function Rivals() {
  const navigate = useNavigate();
  const userName = getCachedName();
  const rivals = useMemo(() => getRivals(), []);
  const matches = useMemo(() => getMatches(), []);
  const top = rivals.slice(0, 4);

  return (
    <div className="min-h-screen bg-background safe-top safe-bottom pb-24" data-testid="rivals-page">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-card/95 backdrop-blur border-b border-border px-4 py-3 flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate('/')} className="h-9 w-9" data-testid="rivals-back">
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <h1 className="font-heading text-xl font-extrabold uppercase tracking-tight flex-1">Rivals — H2H</h1>
      </div>

      <div className="px-4 py-5 space-y-4 max-w-2xl mx-auto">
        {!userName ? (
          <Card>
            <CardContent className="p-8 text-center text-sm">
              <Users className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
              <p className="text-muted-foreground mb-4">Play a Quick Match first — your name (Side A) becomes the anchor for H2H tracking.</p>
              <Button onClick={() => navigate('/play')} data-testid="rivals-start-cta"><Plus className="h-4 w-4 mr-1" /> Start Match</Button>
            </CardContent>
          </Card>
        ) : top.length === 0 ? (
          <Card>
            <CardContent className="p-8 text-center text-sm">
              <Swords className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
              <p className="text-muted-foreground">No rivals yet. Play matches with the same opponents to see your H2H breakdown here.</p>
              <p className="text-[11px] text-muted-foreground mt-2">Currently tracking as: <span className="font-bold">{userName}</span></p>
            </CardContent>
          </Card>
        ) : (
          <>
            <div className="flex items-center justify-between">
              <p className="text-xs text-muted-foreground">Tracking as <span className="font-bold text-foreground">{userName}</span></p>
              <Badge variant="outline" className="text-[10px]">{rivals.length} rivals</Badge>
            </div>
            <div className="space-y-3" data-testid="rivals-list">
              {top.map((r, i) => {
                const winRate = r.played > 0 ? Math.round((r.wins / r.played) * 100) : 0;
                const dominant = r.wins > r.losses;
                return (
                  <Card key={r.name} className={`overflow-hidden ${i === 0 ? 'border-2 border-primary/50' : ''}`}>
                    <CardContent className="p-4 space-y-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center font-heading font-bold ${i === 0 ? 'bg-primary text-primary-foreground' : 'bg-muted'}`}>
                            #{i + 1}
                          </div>
                          <div>
                            <p className="font-heading font-bold text-base">{r.displayName}</p>
                            <p className="text-[11px] text-muted-foreground">Last: {new Date(r.lastPlayed).toLocaleDateString()}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Played</p>
                          <p className="font-heading text-2xl font-extrabold">{r.played}</p>
                        </div>
                      </div>

                      {/* H2H bar */}
                      <div className="flex h-8 rounded-full overflow-hidden bg-muted">
                        <div
                          className="bg-primary flex items-center justify-center text-xs font-bold text-primary-foreground transition-all"
                          style={{ width: `${(r.wins / Math.max(r.played, 1)) * 100}%` }}
                        >
                          {r.wins > 0 && `${r.wins}W`}
                        </div>
                        <div
                          className="bg-muted-foreground/40 flex items-center justify-center text-xs font-bold transition-all"
                          style={{ width: `${(r.losses / Math.max(r.played, 1)) * 100}%` }}
                        >
                          {r.losses > 0 && `${r.losses}L`}
                        </div>
                      </div>

                      <div className="flex items-center justify-between text-xs">
                        <span className="text-muted-foreground">Win rate vs {r.displayName.split(' ')[0]}</span>
                        <span className={`font-bold flex items-center gap-1 ${dominant ? 'text-primary' : 'text-muted-foreground'}`}>
                          {dominant ? <TrendingUp className="h-3.5 w-3.5" /> : <TrendingDown className="h-3.5 w-3.5" />}
                          {winRate}%
                        </span>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            {rivals.length > 4 && (
              <p className="text-center text-xs text-muted-foreground">
                Showing top 4 of {rivals.length} rivals
              </p>
            )}
          </>
        )}
      </div>
      <BottomNav />
    </div>
  );
}
